import { useState, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Paper,
  CircularProgress, Alert, Divider, Radio, RadioGroup,
  FormControlLabel, FormControl, Checkbox, FormGroup,
  Stepper, Step, StepLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import PortalProfileForm from './PortalProfileForm';
import PortalIdentityConfirm from './PortalIdentityConfirm';
import PortalApplicantConfirm from './PortalApplicantConfirm';
import PortalCaptationStep, {
  getCaptationMissing, buildCaptationPayload, needsForeignDocs, needsTraslado, FOREIGN_DOC_KEYS,
} from './PortalCaptationStep';
import { readFairAnswers, saveFairAnswers } from './fairMemory';

// Etiquetas del stepper por paso
const STEP_LABEL = {
  profile: 'portal.stepPersonalData',
  captation: 'portal.massive.stepCaptation',
  filtering: 'portal.stepQuestions',
};
// Datos del perfil que una vacante masiva puede exigir (según su flujo)
const MASSIVE_PROFILE_FIELDS = ['names', 'firstLastName', 'phone', 'salarioPretendido'];

// Feria (PLAN_FERIAS, fase 2): de lo recordado en la feria, solo lo que ESTA
// vacante pregunta (comuna y disponibilidad si están en su formulario;
// documentos si los pide para esa nacionalidad)
const pickReusable = (form, remembered, nationality) => {
  const fieldKeys = (form.fields || []).map((f) => f.key);
  const reused = {};
  ['comuna', 'disponibilidad'].forEach((key) => {
    if (fieldKeys.includes(key) && remembered[key]) reused[key] = remembered[key];
  });
  if (needsForeignDocs(form, nationality)) {
    FOREIGN_DOC_KEYS.forEach((key) => { if (remembered[key]) reused[key] = remembered[key]; });
  }
  return reused;
};

const PortalApplicationScreen = ({
  position,
  candidateId,
  candidateData,
  sessionToken,
  positionId,
  // Feria desde la que se postula (PLAN_FERIAS): la postulación queda marcada con ella
  fairSlug = null,
  // La persona ya está en la vacante porque la agregó el reclutador y viene a
  // completar su postulación (2026-10-08): mismo formulario, con un aviso
  completing = false,
  onSuccess,
  onAlreadyApplied,
}) => {
  const { t } = useTranslation();

  const filteringQuestions = position?.filteringQuestions || [];
  const hasQuestions = positionId && filteringQuestions.length > 0;
  // Vacante masiva con formulario: las preguntas de captación del bot como
  // formulario (PLAN_MASIVO_PORTAL, fase 2)
  const portalForm = positionId ? (position?.portalForm || null) : null;
  // La nacionalidad se declara al entrar al portal: el paso del cargo no la
  // vuelve a preguntar (y se salta si no queda nada que preguntar)
  const hideNationality = Boolean(candidateData?.nationality);

  // Feria (PLAN_FERIAS, fase 2): la comuna, la disponibilidad y los documentos
  // de extranjeros respondidos en una postulación anterior de la MISMA feria
  // (este navegador, por cuenta, 24 h) se reutilizan sin volver a mostrarlos;
  // "Editar" los vuelve a mostrar. Fuera de la feria no cambia nada.
  const [fairReused] = useState(() => (
    fairSlug && portalForm
      ? pickReusable(portalForm, readFairAnswers(candidateId, fairSlug), candidateData?.nationality)
      : {}
  ));
  const [hiddenKeys, setHiddenKeys] = useState(() => Object.keys(fairReused));
  const [captationAnswers, setCaptationAnswers] = useState(() => ({
    ...(candidateData?.nationality ? { nacionalidad: candidateData.nationality } : {}),
    ...fairReused,
  }));
  const docsHidden = FOREIGN_DOC_KEYS.some((key) => hiddenKeys.includes(key));
  const hasCaptationStep = Boolean(portalForm) && (
    (portalForm.fields || []).some((f) => !(hideNationality && f.type === 'nationality') && !hiddenKeys.includes(f.key))
    || (needsForeignDocs(portalForm, candidateData?.nationality) && !docsHidden)
    // Comuna reutilizada distinta a la del cargo: el traslado se pregunta igual
    || (hiddenKeys.includes('comuna') && needsTraslado(portalForm, captationAnswers.comuna))
  );

  // Pasos: datos personales → preguntas del cargo (masivas) → preguntas filtradoras
  const stepKeys = ['profile', ...(hasCaptationStep ? ['captation'] : []), ...(hasQuestions ? ['filtering'] : [])];
  const [step, setStep] = useState(0);
  const currentStep = stepKeys[step] || 'profile';
  const isLastStep = step >= stepKeys.length - 1;
  const [showCaptationErrors, setShowCaptationErrors] = useState(false);

  const [profile, setProfile] = useState({
    name: candidateData?.name || '',
    // Partes del nombre (regla global: name se compone desde estas)
    names: candidateData?.names || '',
    firstLastName: candidateData?.firstLastName || '',
    secondLastName: candidateData?.secondLastName || '',
    phone: candidateData?.phone || '',
    rut: candidateData?.rut || '',
    country: candidateData?.country || '',
    profession: candidateData?.profession || '',
    currentPosition: candidateData?.currentPosition || '',
    salarioPretendido: candidateData?.salarioPretendido || '',
  });

  const [cvFile, setCvFile] = useState(null);
  const [cvUrl, setCvUrl] = useState(candidateData?.cvUrl || null);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Confirmación de identidad: la ficha del proyecto con su documento tiene
  // otro email (PLAN_MASIVO_PORTAL, fase 0). { maskedEmail, blocked } o null
  // (blocked: la ficha es de otra cuenta, no hay código que pedir)
  const [identity, setIdentity] = useState(null);
  // Ya se confirmó una vez: si el backend vuelve a pedirla, error (sin bucle)
  const identityConfirmed = useRef(false);
  // "¿Eres tú?" (PLAN_IMPORTACION_COMPUTRABAJO, fase 0): ficha del proceso con
  // su correo y sin RUT. { name } o null; la respuesta viaja en el reintento
  const [confirmApplicant, setConfirmApplicant] = useState(null);
  const applicantConfirmation = useRef(null);
  // "No tengo acceso a ese correo" / ficha de otra cuenta: postula igual y la
  // postulación queda "Por validar"
  const identityFallback = useRef(false);
  const interrupted = Boolean(identity || confirmApplicant);

  const handleProfile = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleMultipleChoice = (questionId, option, checked) => {
    setAnswers((prev) => {
      const current = prev[questionId] || [];
      if (checked) return { ...prev, [questionId]: [...current, option] };
      return { ...prev, [questionId]: current.filter((o) => o !== option) };
    });
  };

  const handleCvChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      setError(t('portal.cvInvalidType'));
      return;
    }

    setCvFile(file);
    setError(null);
    setUploadingCv(true);

    try {
      const formDataCv = new FormData();
      formDataCv.append('cv', file);

      const response = await api.post(
        `/public/candidates/me/cv`,
        formDataCv,
        {
          headers: {
            'x-candidate-token': sessionToken,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.response?.status_response === 'OK') {
        setCvUrl(response.data.response.cvUrl);
      } else {
        setError(t('portal.cvUploadError'));
        setCvFile(null);
      }
    } catch (err) {
      console.error(err);
      setError(t('portal.cvUploadError'));
      setCvFile(null);
    } finally {
      setUploadingCv(false);
    }
  };

  const validateProfile = () => {
    if (!profile.name.trim()) {
      setError(t('portal.nameRequired'));
      return false;
    }
    // Masivas: los datos que el flujo de captación pide como obligatorios
    if (portalForm) {
      const required = portalForm.profile || {};
      const missing = MASSIVE_PROFILE_FIELDS.filter(
        (field) => required[field]?.required && !String(profile[field] || '').trim()
      );
      if (required.cv?.required && !cvUrl) missing.push('cv');
      if (missing.length > 0) {
        setError(t('portal.massive.profileRequired', { fields: missing.map((f) => t(`portal.${f}`)).join(', ') }));
        return false;
      }
    }
    setError(null);
    return true;
  };

  const validateCaptation = () => {
    if (!portalForm) return true;
    if (getCaptationMissing(portalForm, captationAnswers).length > 0) {
      setShowCaptationErrors(true);
      setError(t('portal.massive.requiredFields'));
      return false;
    }
    setError(null);
    return true;
  };

  const handleCaptationChange = (key, value) => {
    setCaptationAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const handleNextStep = async () => {
    if (currentStep === 'profile') {
      if (!validateProfile()) return;
      // Guardar perfil antes de pasar al siguiente paso
      try {
        await api.put(
          `/public/candidates/me`,
          profile,
          { headers: { 'x-candidate-token': sessionToken } }
        );
      } catch (err) {
        console.error(err);
        setError(t('portal.applicationError'));
        return;
      }
    }
    if (currentStep === 'captation' && !validateCaptation()) return;
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    if (!validateProfile()) return;
    if (!validateCaptation()) return;

    setSaving(true);
    setError(null);
    try {
      // Guardar perfil (en caso de que no haya pasado por el step anterior)
      await api.put(
        `/public/candidates/me`,
        profile,
        { headers: { 'x-candidate-token': sessionToken } }
      );

      // Crear application si hay positionId
      if (positionId) {
        const appPayload = {
          candidateId,
          positionId,
          source: 'portal',
          filteringAnswers: Object.entries(answers).map(([questionId, answer]) => ({
            questionId,
            answer,
          })),
          ...(portalForm ? { captationAnswers: buildCaptationPayload(portalForm, captationAnswers) } : {}),
          ...(fairSlug ? { fairSlug } : {}),
          ...(applicantConfirmation.current ? { applicantConfirmation: applicantConfirmation.current } : {}),
          ...(identityFallback.current ? { identityFallback: true } : {}),
        };

        const appResponse = await api.post(
          `/applications`,
          appPayload,
          { headers: { 'x-candidate-token': sessionToken } }
        );

        const responseData = appResponse.data.response;

        // Completó la postulación que le creó el reclutador (no se crea otra)
        if (responseData?.status_response === 'OK' && responseData?.completed) {
          onSuccess({ completed: true });
          return;
        }

        // Ya existía una postulación a esta vacante: mostrar "Ya postulaste"
        // en vez de un falso "¡Postulación enviada!" (el backend no crea duplicado)
        if (responseData?.status_response === 'ALREADY_EXISTS') {
          onAlreadyApplied?.(responseData.data || null);
          return;
        }

        if (responseData?.status_response !== 'OK') {
          setError(t('portal.applicationError'));
          return;
        }

        // Feria: recordar comuna, disponibilidad y documentos para la siguiente
        // vacante de la misma feria (PLAN_FERIAS, fase 2)
        if (fairSlug && portalForm) {
          saveFairAnswers(candidateId, fairSlug, buildCaptationPayload(portalForm, captationAnswers));
        }

        // "Por validar": la pantalla final avisa que un reclutador revisará sus datos
        if (responseData?.pendingValidation) {
          onSuccess({ pendingValidation: true });
          return;
        }
      }

      onSuccess();
    } catch (err) {
      console.error(err);
      const errResponse = err.response?.data?.response;
      if (err.response?.status === 409 && errResponse?.errorType === 'CONFIRM_APPLICANT' && !applicantConfirmation.current) {
        // Ficha del proceso con su correo y sin RUT: "¿Eres tú?" y se reintenta
        setConfirmApplicant({ name: errResponse.applicantName || null });
      } else if (err.response?.status === 409 && errResponse?.errorType === 'IDENTITY_REQUIRED' && !identityConfirmed.current) {
        // La ficha del proyecto con su documento tiene otro email: se confirma
        // con el código enviado a ese email y se reintenta (o postula igual)
        setIdentity({ maskedEmail: errResponse.maskedEmail || null, blocked: false });
      } else if (err.response?.status === 409 && errResponse?.errorType === 'APPLICANT_LINKED_ELSEWHERE') {
        // La ficha es de otra cuenta: no hay código que pedir, postula igual
        setIdentity({ maskedEmail: null, blocked: true });
      } else if (errResponse?.code === 'APPLICANT_UNRESOLVED' || errResponse?.errorType === 'IDENTITY_REQUIRED') {
        setError(t('portal.applicationUnresolved'));
      } else if (errResponse?.errorType === 'POSITION_NOT_ACCEPTING' || errResponse?.errorType === 'POSITION_LIMIT_REACHED') {
        setError(t('portal.massive.notAccepting'));
      } else if (errResponse?.errorType === 'MISSING_ANSWERS') {
        // Si falta algo, se vuelve a mostrar también lo reutilizado de la feria
        setHiddenKeys([]);
        setShowCaptationErrors(true);
        setError(t('portal.massive.missingAnswers'));
      } else {
        setError(t('portal.applicationError'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Código confirmado: la ficha quedó vinculada a la cuenta → reintentar
  const handleIdentityConfirmed = () => {
    identityConfirmed.current = true;
    setIdentity(null);
    handleSubmit();
  };

  // Sin acceso al correo de la ficha: postula igual ("Por validar")
  const handleApplyAnyway = () => {
    identityFallback.current = true;
    setIdentity(null);
    handleSubmit();
  };

  // Respuesta a "¿Eres tú?" ('yes' | 'no') → reintentar
  const handleApplicantAnswer = (answer) => {
    applicantConfirmation.current = answer;
    setConfirmApplicant(null);
    handleSubmit();
  };

  const steps = stepKeys.length > 1 ? stepKeys.map((k) => t(STEP_LABEL[k])) : null;

  // Feria: resumen de lo reutilizado, con "Editar" para volver a mostrarlo. Va en
  // "Preguntas del cargo" o, si ese paso no queda, en "Datos personales"
  const reusedSummary = hiddenKeys.length > 0 ? (
    <Alert
      severity="info"
      sx={{ mb: 3 }}
      data-testid="fair-reused"
      action={(
        <Button color="inherit" size="small" onClick={() => setHiddenKeys([])}>
          {t('portal.fair.edit')}
        </Button>
      )}
    >
      <Typography variant="body2" fontWeight={600}>{t('portal.fair.reusedTitle')}</Typography>
      {hiddenKeys.includes('comuna') && (
        <Typography variant="body2">{t('portal.massive.fields.comuna')}: {captationAnswers.comuna}</Typography>
      )}
      {hiddenKeys.includes('disponibilidad') && (
        <Typography variant="body2">{t('portal.massive.fields.disponibilidad')}: {captationAnswers.disponibilidad}</Typography>
      )}
      {docsHidden && (
        <Typography variant="body2">
          {t('portal.fair.reusedDocs', { count: FOREIGN_DOC_KEYS.filter((key) => captationAnswers[key]).length })}
        </Typography>
      )}
    </Alert>
  ) : null;

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: 2, py: 4 }}>

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          component="img"
          src="/amandalogoh.png"
          alt="Amanda HR"
          sx={{ height: 40, mb: 3 }}
        />
        {position && (
          <>
            <Typography variant="h5" fontWeight={700}>
              {position.name}
            </Typography>
            {position.industria && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {position.industria}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Stepper — solo si hay preguntas */}
      {steps && (
        <Stepper activeStep={step} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {completing && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('portal.completeApplication.hint', { position: position?.name || '' })}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* ── CONFIRMACIÓN DE IDENTIDAD (código al email de la ficha) ── */}
      {identity && (
        <PortalIdentityConfirm
          key={identity.blocked ? 'blocked' : 'code'}
          positionId={positionId}
          sessionToken={sessionToken}
          maskedEmail={identity.maskedEmail}
          onConfirmed={handleIdentityConfirmed}
          onCancel={() => setIdentity(null)}
          onApplyAnyway={handleApplyAnyway}
          startBlocked={Boolean(identity.blocked)}
        />
      )}

      {/* ── "¿ERES TÚ?" (ficha del proceso con su correo y sin RUT) ── */}
      {confirmApplicant && (
        <PortalApplicantConfirm
          applicantName={confirmApplicant.name}
          onAnswer={handleApplicantAnswer}
          onCancel={() => setConfirmApplicant(null)}
          disabled={saving}
        />
      )}

      {/* ── PASO — DATOS PERSONALES ── */}
      {!interrupted && currentStep === 'profile' && (
        <>
          {/* Información del cargo */}
          {position && (
            <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, mb: 3 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                {position.name}
              </Typography>

              {position.industria && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {position.industria}
                </Typography>
              )}

              {position.descripcion && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('portal.positionDescription')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {position.descripcion}
                  </Typography>
                </>
              )}

              {position.responsabilidades && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('portal.positionResponsabilities')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {position.responsabilidades}
                  </Typography>
                </>
              )}

              {position.perfil && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('portal.positionProfile')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {position.perfil}
                  </Typography>
                </>
              )}

              {position.conocimientosExcluyentes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('portal.positionRequiredKnowledge')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {position.conocimientosExcluyentes}
                  </Typography>
                </>
              )}

              {position.conocimientosDeseables && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('portal.positionDesirableKnowledge')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {position.conocimientosDeseables}
                  </Typography>
                </>
              )}

              {/* Beneficios del cargo [{ nombre, descripcion }] */}
              {Array.isArray(position.beneficios) && position.beneficios.some((b) => b?.nombre) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('portal.positionBenefits')}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {position.beneficios.filter((b) => b?.nombre).map((b, i) => (
                      <Typography component="li" variant="body2" color="text.secondary" key={i}>
                        <strong>{b.nombre}</strong>{b.descripcion ? ` — ${b.descripcion}` : ''}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}

              {position.additionalInfoUrl && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Información adicional
                  </Typography>
                  <Button
                    href={position.additionalInfoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                  >
                    Ver información adicional
                  </Button>
                </>
              )}
            </Paper>
          )}

          {/* Datos personales + CV (formulario compartido con "Mi perfil") */}
          <PortalProfileForm
            profile={profile}
            onProfileChange={handleProfile}
            candidateData={candidateData}
            cvFile={cvFile}
            cvUrl={cvUrl}
            uploadingCv={uploadingCv}
            onCvChange={handleCvChange}
            onClearCvFile={() => setCvFile(null)}
          />

          {!hasCaptationStep && reusedSummary}

          {/* Botón */}
          <Button
            variant="contained"
            fullWidth
            onClick={isLastStep ? handleSubmit : handleNextStep}
            disabled={saving || uploadingCv}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5 }}
          >
            {saving
              ? t('portal.applying')
              : !isLastStep
                ? t('portal.next')
                : positionId
                  ? t('portal.apply')
                  : t('portal.saveProfile')
            }
          </Button>
        </>
      )}

      {/* ── PASO — PREGUNTAS DEL CARGO (masivas) ── */}
      {!interrupted && currentStep === 'captation' && (
        <>
          {reusedSummary}
          <PortalCaptationStep
            form={portalForm}
            answers={captationAnswers}
            onChange={handleCaptationChange}
            positionId={positionId}
            sessionToken={sessionToken}
            showErrors={showCaptationErrors}
            hideNationality={hideNationality}
            hiddenKeys={hiddenKeys}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setStep((s) => s - 1)}
              disabled={saving}
              color="inherit"
              sx={{ flex: 1 }}
            >
              {t('portal.back')}
            </Button>
            <Button
              variant="contained"
              onClick={isLastStep ? handleSubmit : handleNextStep}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5, flex: 2 }}
            >
              {saving ? t('portal.applying') : isLastStep ? t('portal.apply') : t('portal.next')}
            </Button>
          </Box>
        </>
      )}

      {/* ── PASO — PREGUNTAS FILTRADORAS ── */}
      {!interrupted && currentStep === 'filtering' && (
        <>
          <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              {t('portal.filteringQuestions')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {filteringQuestions.map((q, i) => (
                <Box key={q.id}>
                  {i > 0 && <Divider sx={{ mb: 3 }} />}
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5 }}>
                    {i + 1}. {q.question}
                  </Typography>

                  {q.type === 'text' && (
                    <TextField
                      value={answers[q.id] || ''}
                      onChange={(e) => handleAnswer(q.id, e.target.value)}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder={t('portal.yourAnswer')}
                    />
                  )}

                  {q.type === 'yes_no' && (
                    <FormControl>
                      <RadioGroup
                        row
                        value={answers[q.id] || ''}
                        onChange={(e) => handleAnswer(q.id, e.target.value)}
                      >
                        <FormControlLabel value="yes" control={<Radio />} label={t('yes')} />
                        <FormControlLabel value="no" control={<Radio />} label={t('no')} />
                      </RadioGroup>
                    </FormControl>
                  )}

                  {q.type === 'multiple_choice' && (
                    <FormGroup>
                      {(q.options || []).map((option) => (
                        <FormControlLabel
                          key={option}
                          control={
                            <Checkbox
                              checked={(answers[q.id] || []).includes(option)}
                              onChange={(e) => handleMultipleChoice(q.id, option, e.target.checked)}
                            />
                          }
                          label={option}
                        />
                      ))}
                    </FormGroup>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Botones */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setStep((s) => s - 1)}
              disabled={saving}
              color="inherit"
              sx={{ flex: 1 }}
            >
              {t('portal.back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5, flex: 2 }}
            >
              {saving ? t('portal.applying') : t('portal.apply')}
            </Button>
          </Box>
        </>
      )}

    </Box>
  );
};

export default PortalApplicationScreen;